import React, { useState } from 'react';

import { func } from 'prop-types';

import { connect } from 'react-redux';

import { Auth } from 'aws-amplify';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import InputAdornment from '@material-ui/core/InputAdornment';

import EsmsCard from '../../components/EsmsCard';
import PasswordRequirements from '../../components/PasswordRequirements';
import CancelButton from '../../components/CancelButton';
import ConfirmButton from '../../components/ConfirmButton';

import { awsErrorMessages } from '../../helpers/awsErrorMessages';

import * as ApplicationMessageActions from '../../actions/applicationMessageActions';

import classes from './UpdatePasswordCard.module.css';

const ValidationSchema = Yup.object().shape({
    password: Yup.string()
        .required(),
    newPassword: Yup.string()
        .min(8)
        .max(99)
        .matches(
            // eslint-disable-next-line no-useless-escape
            /^(?=[0-9a-zA-Z\^\$\*\.\[\]\{\}\(\)\?\-\"\!\@\#\%\&\/\\\,\>\<\'\:\;\|\_\~\`]{6,}$)(?=[^a-z]*[a-z])(?=[^A-Z]*[A-Z])(?=[^0-9]*[0-9])(?=[^\^\$\*\.\[\]\{\}\(\)\?\-\"\!\@\#\%\&\/\\\,\>\<\'\:\;\|\_\~\`]*[\^\$\*\.\[\]\{\}\(\)\?\-\"\!\@\#\%\&\/\\\,\>\<\'\:\;\|\_\~\`]).*/,
            'Password does not match required format.'
        )
        .required('Password required')
});

const UpdatePasswordCard = props => {
    const [showForm, setShowForm] = useState(false);
    const [showPopper, setShowPopper] = useState(false);
    const [anchorEl, setAnchorEl] = useState();

    const formik = useFormik({
        initialValues: {
            password:    '',
            newPassword: ''
        },
        validationSchema: ValidationSchema,
        onSubmit:         async (values, { setSubmitting }) => {
            setSubmitting(true);

            Auth.currentAuthenticatedUser()
                .then(user => {
                    return Auth.changePassword(user, values.password, values.newPassword);
                })
                .then(data => {
                    props.enqueueSnackbar('Password changed successfully.', 'success');

                    setTimeout(() => {
                        props.signOut();
                    }, 3000);
                })
                .catch(err => {
                    let errorMessage;
                    if (err.message) {
                        if (awsErrorMessages[err.message]) {
                            errorMessage = awsErrorMessages[err.message];
                        } else {
                            errorMessage = err.message;
                        }
                    } else {
                        if (awsErrorMessages[err]) {
                            errorMessage = awsErrorMessages[err];
                        } else {
                            errorMessage = err;
                        }
                    }

                    props.enqueueSnackbar(errorMessage, 'error');
                    setSubmitting(false);
                });
        }
    });

    const handleToggleForm = () => {
        formik.handleReset();
        setShowForm(!showForm);
    };

    const handleOpenPopper = (event) => {
        const { currentTarget } = event;
        setAnchorEl(currentTarget);
        setShowPopper(!showPopper);
    };

    const populateUpdatePassword = () => {
        if (showForm) {
            return (
                <div className={classes.formContainer}>
                    <form
                        noValidate
                        autoComplete="off"
                        onSubmit={formik.handleSubmit}
                    >
                        <div className={classes.formRow}>
                            <FormControl className={`${classes.FormControl}`}>
                                <InputLabel
                                    htmlFor="password"
                                    classes={{
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }}
                                    error={Boolean(formik.errors.password) && Boolean(formik.touched.password)}
                                >
                                    Password
                                </InputLabel>
                                <Input
                                    id="password"
                                    name="password"
                                    value={formik.values.password}
                                    onChange={formik.handleChange}
                                    type="password"
                                    classes={{
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }}
                                    error={Boolean(formik.errors.password) && Boolean(formik.touched.password)}
                                />
                                {!formik.errors.password && <FormHelperText className={classes.helper}>Enter your current password.</FormHelperText>}
                                {formik.touched.password &&
                                 formik.errors.password && <FormHelperText className={classes.error}>{formik.errors.password}</FormHelperText>}
                            </FormControl>
                        </div>
                        <div className={classes.formRow}>
                            <FormControl className={`${classes.FormControl}`}>
                                <InputLabel
                                    htmlFor="newPassword"
                                    classes={{
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }}
                                    error={Boolean(formik.errors.newPassword) && Boolean(formik.touched.newPassword)}
                                >
                                    New Password
                                </InputLabel>
                                <Input
                                    id="newPassword"
                                    name="newPassword"
                                    value={formik.values.newPassword}
                                    onChange={formik.handleChange}
                                    type="password"
                                    classes={{
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }}
                                    error={Boolean(formik.errors.newPassword) && Boolean(formik.touched.newPassword)}
                                    endAdornment={
                                        <InputAdornment
                                            onClick={(event) => handleOpenPopper(event)}
                                            position="end"
                                        >
                                            <i className="fas fa-question-circle" />
                                        </InputAdornment>
                                    }
                                />
                                {!formik.errors.password && <FormHelperText className={classes.helper}>Enter your new password.</FormHelperText>}
                                {formik.touched.newPassword &&
                                 formik.errors.newPassword && <FormHelperText className={classes.error}>{formik.errors.newPassword}</FormHelperText>}
                            </FormControl>
                            <PasswordRequirements
                                open={showPopper}
                                anchorEl={anchorEl}
                                placement="left"
                            />
                        </div>

                        <div className={classes.buttonContainer}>
                            <CancelButton cancel={handleToggleForm} />
                            <ConfirmButton
                                label="Update"
                                disabled={formik.isSubmitting}
                                className={classes.updateButton}
                            />
                        </div>
                    </form>
                </div>
            );
        }

        return (
            <div className={classes.innerContainer}>
                <p>************</p>

                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleToggleForm}
                >
                    Update Password
                </Button>
            </div>
        );
    };

    return (
        <EsmsCard
            title="Update Password"
            imagePath="/assets/images/lockFlat.svg"
        >
            {populateUpdatePassword()}
        </EsmsCard>
    );
};

UpdatePasswordCard.defaultProps = {
    enqueueSnackbar: () => { }
};

UpdatePasswordCard.propTypes = {
    enqueueSnackbar: func
};

const mapDispatchToProps = {
    ...ApplicationMessageActions
};

export default connect(null, mapDispatchToProps)(UpdatePasswordCard);
